import type { ReactNode } from "react";
import { BALANCE, teamsInfo, wearLevel, wearPercent, type ModuleState, type StationState } from "@station-keeping/sim";
import { useGameUi } from "../../game/store";
import { formatClock, formatDuration, t } from "../../i18n";
import { cssColor, stageColor } from "../../render/palette";
import { failureLabel, moduleName, stageLabel } from "../labels";
import { PixelText } from "../pixel";
import { EmptyState, Led, Plate, SectionTitle, WearGauge } from "../primitives";
import { ModuleThumb } from "../thumbs";
import { MaintenanceActions } from "./MaintenanceActions";

/** Trois voyants pour les trois paliers d'une panne ; le dernier clignote. */
export function StageLeds({ stage }: { stage: 1 | 2 | 3 }) {
  return (
    <span className="flex items-center gap-[var(--px)]" aria-label={stageLabel(stage)}>
      {([1, 2, 3] as const).map((level) => (
        <Led key={level} on={level <= stage} blink={stage === 3 && level === 3} color={cssColor(stageColor(level))} />
      ))}
    </span>
  );
}

function ModuleButton({ module, children }: { module: ModuleState; children: ReactNode }) {
  const select = useGameUi((ui) => ui.select);
  return (
    <button type="button" onClick={() => select(module.id)} className="flex w-full items-center gap-3 text-left hover:bg-face-2 focus-visible:bg-face-2">
      <span className="grid w-[3.75rem] shrink-0 place-items-center">
        <ModuleThumb type={module.type} size="sm" />
      </span>
      {children}
    </button>
  );
}

/**
 * Le triage, comme un tableau des scores : les pannes de la plus grave à la plus légère, puis les modules
 * usés à surveiller. Chaque rangée a ses touches d'intervention.
 */
export function FailuresPanel({ state }: { state: StationState }) {
  const openPanel = useGameUi((ui) => ui.openPanel);
  const teams = teamsInfo(state);
  const failed = state.modules
    .filter((module) => module.failure)
    .sort((a, b) => (b.failure?.stage ?? 0) - (a.failure?.stage ?? 0) || (a.failure?.startedAt ?? 0) - (b.failure?.startedAt ?? 0));
  const worn = state.modules
    .filter((module) => module.type !== "core" && !module.failure && module.buildLeft === 0 && module.wear >= BALANCE.wear.worn)
    .sort((a, b) => b.wear - a.wear);

  return (
    <div>
      <Plate title={t("failures.title")} onClose={() => openPanel(null)}>
        <p className="mt-1.5 text-[0.9375rem] text-muted">{t("failures.triageHint")}</p>
        <p className={`mt-1 flex items-center gap-2 text-[0.9375rem] ${teams.free === 0 ? "text-warn" : "text-ink"}`}>
          <Led on={teams.free > 0} color="var(--color-good)" />
          {t("failures.teams", { count: teams.free, free: teams.free, total: teams.total })}
        </p>
      </Plate>

      {failed.length === 0 ? (
        <div className="mt-3">
          <EmptyState>{t("failures.none")}</EmptyState>
        </div>
      ) : (
        <ol>
          {failed.map((module) => {
            const failure = module.failure!;
            const since = state.tick - failure.startedAt;
            return (
              <li key={module.id} className="dotted py-2.5">
                <ModuleButton module={module}>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-base font-semibold text-ink">{moduleName(module.type)}</span>
                      <StageLeds stage={failure.stage} />
                    </span>
                    <span className="mt-0.5 flex items-center justify-between gap-2 text-[0.875rem]">
                      <span style={{ color: cssColor(stageColor(failure.stage)) }}>
                        {failureLabel(failure.kind)} · {stageLabel(failure.stage)}
                      </span>
                      <span className="text-muted">
                        <PixelText text={formatClock(since)} label={t("failures.since", { time: formatDuration(since) })} />
                      </span>
                    </span>
                  </span>
                </ModuleButton>
                <div className="mt-2 pl-[4.5rem]">
                  <MaintenanceActions state={state} module={module} compact />
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {worn.length > 0 ? (
        <>
          <SectionTitle hint={t("failures.watchHint")}>{t("failures.watchTitle")}</SectionTitle>
          <ol>
            {worn.map((module) => {
              const level = wearLevel(module.wear);
              const color = level === "critical" ? "var(--color-alert)" : "var(--color-warn)";
              return (
                <li key={module.id} className="dotted py-2.5">
                  <ModuleButton module={module}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold text-ink">{moduleName(module.type)}</span>
                      <span className="mt-1 flex items-center gap-2">
                        <WearGauge wear={module.wear / BALANCE.wear.max} color={color} level={level} label={t("wear.label")} />
                        <span style={{ color }}>
                          <PixelText text={`${wearPercent(module.wear)}%`} label={`${wearPercent(module.wear)} %`} />
                        </span>
                      </span>
                    </span>
                  </ModuleButton>
                  <div className="mt-2 pl-[4.5rem]">
                    <MaintenanceActions state={state} module={module} compact />
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}
    </div>
  );
}
